import type { ChatScrollPosition } from "@ext/agent/components/utils/chatScrollMemory";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const BOTTOM_THRESHOLD = 100;

const isNearBottom = (root: HTMLElement, top = root.scrollTop) =>
	root.scrollHeight - top - root.clientHeight <= BOTTOM_THRESHOLD;

export type ChatScroller = ReturnType<typeof useChatScroller>["scroller"];

export const useChatScroller = (onPositionChange: (position: ChatScrollPosition) => void) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const isUserScrolledUp = useRef(false);
	const isProgrammaticScroll = useRef(false);
	const programmaticTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onPositionChangeRef = useRef(onPositionChange);
	onPositionChangeRef.current = onPositionChange;
	const [isAtBottom, setIsAtBottom] = useState(true);

	const scrollTo = useCallback((top: number, behavior: ScrollBehavior) => {
		const root = rootRef.current;
		if (!root) return;
		const atBottom = isNearBottom(root, top);
		isUserScrolledUp.current = !atBottom;
		isProgrammaticScroll.current = true;
		root.scrollTo({ top, behavior });
		if (behavior === "instant") setIsAtBottom(atBottom);
		if (programmaticTimeoutRef.current !== null) clearTimeout(programmaticTimeoutRef.current);
		programmaticTimeoutRef.current = setTimeout(
			() => {
				isProgrammaticScroll.current = false;
			},
			behavior === "instant" ? 0 : 100,
		);
	}, []);

	const scrollToEnd = useCallback(
		(behavior: ScrollBehavior) => {
			const root = rootRef.current;
			if (!root) return;
			scrollTo(root.scrollHeight - root.clientHeight, behavior);
		},
		[scrollTo],
	);

	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;

		let rafId: number | null = null;

		const syncPosition = () => {
			rafId = null;
			const top = root.scrollTop;
			const atBottom = isNearBottom(root, top);
			onPositionChangeRef.current({ top, atBottom });
			setIsAtBottom(atBottom);
		};

		const onScroll = () => {
			if (!isProgrammaticScroll.current) isUserScrolledUp.current = !isNearBottom(root);
			if (rafId === null) rafId = requestAnimationFrame(syncPosition);
		};

		const onScrollEnd = () => {
			isProgrammaticScroll.current = false;
		};

		root.addEventListener("scroll", onScroll, { passive: true });
		root.addEventListener("scrollend", onScrollEnd, { passive: true });
		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
			if (programmaticTimeoutRef.current !== null) clearTimeout(programmaticTimeoutRef.current);
			root.removeEventListener("scroll", onScroll);
			root.removeEventListener("scrollend", onScrollEnd);
		};
	}, []);

	const scroller = useMemo(() => ({ rootRef, isUserScrolledUp, scrollTo, scrollToEnd }), [scrollTo, scrollToEnd]);

	return { scroller, isAtBottom };
};
